import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonModal,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToast,
  IonToolbar,
} from "@ionic/react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { apiFetch } from "../api/client.js";
import { fetchClients } from "../api/clients.js";
import { fetchUsers } from "../api/users.js";
import PdfInvoicePreview from "../components/PdfInvoicePreview.jsx";
import PdfPayRunPreview from "../components/PdfPayRunPreview.jsx";
import PaymentsPanel from "../components/PaymentsPanel.jsx";
import SearchSelect from "../components/SearchSelect.jsx";
import { currentWeekRange } from "../utils/date.js";
import { downloadAuthenticatedPdf } from "../utils/downloadPdf.js";

const singleInvoiceBlank = {
  clientIds: [],
  startDate: "",
  endDate: "",
};

const combinedInvoiceDetailsBlank = {
  recipientName: "",
  recipientAddress: "",
  recipientEmail: "",
  recipientPhone: "",
};

const batchInvoiceBlank = {
  startDate: "",
  endDate: "",
  clientIds: [],
};

const singlePayRunBlank = {
  userId: "",
  startDate: "",
  endDate: "",
};

const batchPayRunBlank = {
  startDate: "",
  endDate: "",
  userIds: [],
};

function dueDate() {
  const value = new Date();

  value.setDate(value.getDate() + 14);

  return value;
}

function payDate() {
  return new Date();
}

function dateStart(value) {
  return new Date(`${value}T00:00:00`);
}

function dateEnd(value) {
  return new Date(`${value}T23:59:59`);
}

export default function FinancePage() {
  const history = useHistory();
  const [segment, setSegment] = useState("invoice");
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [singleInvoiceForm, setSingleInvoiceForm] = useState(singleInvoiceBlank);
  const [combinedInvoiceDetails, setCombinedInvoiceDetails] = useState(combinedInvoiceDetailsBlank);
  const [batchInvoiceForm, setBatchInvoiceForm] = useState(batchInvoiceBlank);
  const [singlePayRunForm, setSinglePayRunForm] = useState(singlePayRunBlank);
  const [batchPayRunForm, setBatchPayRunForm] = useState(batchPayRunBlank);
  const [batchInvoiceCandidates, setBatchInvoiceCandidates] = useState([]);
  const [batchPayRunCandidates, setBatchPayRunCandidates] = useState([]);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [previewPayRun, setPreviewPayRun] = useState(null);
  const [toast, setToast] = useState("");
  const [isSingleInvoiceOpen, setIsSingleInvoiceOpen] = useState(false);
  const [isCombinedInvoiceDetailsOpen, setIsCombinedInvoiceDetailsOpen] = useState(false);
  const [isBatchInvoiceOpen, setIsBatchInvoiceOpen] = useState(false);
  const [isSinglePayRunOpen, setIsSinglePayRunOpen] = useState(false);
  const [isBatchPayRunOpen, setIsBatchPayRunOpen] = useState(false);
  const [isGeneratingInvoicePreview, setIsGeneratingInvoicePreview] = useState(false);
  const [isGeneratingPayRunPreview, setIsGeneratingPayRunPreview] = useState(false);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  const [isSendingPayRun, setIsSendingPayRun] = useState(false);
  const [isCreatingInvoiceBatch, setIsCreatingInvoiceBatch] = useState(false);
  const [isCreatingPayRunBatch, setIsCreatingPayRunBatch] = useState(false);

  async function load() {
    const [clientList, userList] = await Promise.all([fetchClients(), fetchUsers()]);
    setClients(clientList);
    setUsers(userList);
  }

  useEffect(() => {
    load().catch((err) => setToast(err.message));
  }, []);

  function openSingleInvoice() {
    setSingleInvoiceForm(singleInvoiceBlank);
    setCombinedInvoiceDetails(combinedInvoiceDetailsBlank);
    setPreviewInvoice(null);
    setIsGeneratingInvoicePreview(false);
    setIsSingleInvoiceOpen(true);
  }

  function selectedInvoiceClients() {
    return singleInvoiceForm.clientIds
      .map((clientId) => clients.find((client) => client.id === clientId))
      .filter(Boolean);
  }

  function combinedOptionValue(client, value = "") {
    return `${client.id}::${value || ""}`;
  }

  function combinedValueFromOption(value) {
    return value.split("::").slice(1).join("::");
  }

  function selectedClientOptions(field) {
    return selectedInvoiceClients()
      .map((client) => {
        const rawValue = field(client);

        if (rawValue === undefined || rawValue === null || rawValue === "") {
          return null;
        }

        return { value: combinedOptionValue(client, String(rawValue)), label: String(rawValue) };
      })
      .filter(Boolean);
  }

  function combinedSelectedValue(value, options) {
    return options.find((option) => combinedValueFromOption(option.value) === String(value || ""))?.value || "";
  }

  function defaultCombinedInvoiceDetails() {
    const primaryClient = selectedInvoiceClients()[0];

    if (!primaryClient) {
      return combinedInvoiceDetailsBlank;
    }

    return {
      recipientName: primaryClient.invoiceName || primaryClient.name,
      recipientAddress: primaryClient.address || "",
      recipientEmail: primaryClient.email || "",
      recipientPhone: primaryClient.phone || "",
    };
  }

  function openSinglePayRun() {
    setSinglePayRunForm(singlePayRunBlank);
    setPreviewPayRun(null);
    setIsGeneratingPayRunPreview(false);
    setIsSinglePayRunOpen(true);
  }

  async function createInvoicePreview(body, endpoint = "/invoices") {
    try {
      setIsGeneratingInvoicePreview(true);
      setPreviewInvoice(null);

      const data = await apiFetch(endpoint, {
        method: "POST",
        body,
      });

      setPreviewInvoice(data);
      await load();
    } catch (err) {
      setToast(err.message);
    } finally {
      setIsGeneratingInvoicePreview(false);
    }
  }

  async function generateSingleInvoicePreview() {
    if (singleInvoiceForm.clientIds.length > 1) {
      setCombinedInvoiceDetails(defaultCombinedInvoiceDetails());
      setIsCombinedInvoiceDetailsOpen(true);

      return;
    }

    await createInvoicePreview({
      clientId: singleInvoiceForm.clientIds[0],
      startDate: dateStart(singleInvoiceForm.startDate),
      endDate: dateEnd(singleInvoiceForm.endDate),
      dueDate: dueDate(),
    });
  }

  async function generateCombinedInvoicePreview() {
    await createInvoicePreview(
      {
        clientIds: singleInvoiceForm.clientIds,
        startDate: dateStart(singleInvoiceForm.startDate),
        endDate: dateEnd(singleInvoiceForm.endDate),
        dueDate: dueDate(),
        recipientName: combinedInvoiceDetails.recipientName,
        recipientAddress: combinedInvoiceDetails.recipientAddress,
        recipientEmail: combinedInvoiceDetails.recipientEmail,
        recipientPhone: combinedInvoiceDetails.recipientPhone,
      },
      "/invoices/combined",
    );
    setIsCombinedInvoiceDetailsOpen(false);
  }

  async function generateSinglePayRunPreview() {
    try {
      setIsGeneratingPayRunPreview(true);
      setPreviewPayRun(null);

      const data = await apiFetch("/pay-runs", {
        method: "POST",
        body: {
          userId: singlePayRunForm.userId,
          startDate: dateStart(singlePayRunForm.startDate),
          endDate: dateEnd(singlePayRunForm.endDate),
          payDate: payDate(),
        },
      });

      setPreviewPayRun(data);
      await load();
    } catch (err) {
      setToast(err.message);
    } finally {
      setIsGeneratingPayRunPreview(false);
    }
  }

  async function sendInvoice(invoiceId) {
    try {
      setIsSendingInvoice(true);
      await apiFetch(`/invoices/send/${invoiceId}`, { method: "POST" });
      setSingleInvoiceForm(singleInvoiceBlank);
      setCombinedInvoiceDetails(combinedInvoiceDetailsBlank);
      setPreviewInvoice(null);
      setIsSingleInvoiceOpen(false);
      await load();
      setToast("Invoice sent.");
    } catch (err) {
      setToast(err.message);
    } finally {
      setIsSendingInvoice(false);
    }
  }

  async function sendPayRun(payRunId) {
    try {
      setIsSendingPayRun(true);
      await apiFetch(`/pay-runs/send/${payRunId}`, { method: "POST" });
      setSinglePayRunForm(singlePayRunBlank);
      setPreviewPayRun(null);
      setIsSinglePayRunOpen(false);
      await load();
      setToast("Pay run sent.");
    } catch (err) {
      setToast(err.message);
    } finally {
      setIsSendingPayRun(false);
    }
  }

  async function downloadInvoice(invoice) {
    if (!invoice) {
      return;
    }

    try {
      await downloadAuthenticatedPdf(`/invoices/pdf/${invoice.id}`, `${invoice.invoiceNumber}.pdf`);
    } catch (err) {
      setToast(err.message);
    }
  }

  async function downloadPayRun(payRun) {
    if (!payRun) {
      return;
    }

    try {
      await downloadAuthenticatedPdf(`/pay-runs/pdf/${payRun.id}`, `${payRun.payRunNumber}.pdf`);
    } catch (err) {
      setToast(err.message);
    }
  }

  async function loadBatchInvoiceCandidates(nextForm = batchInvoiceForm) {
    if (!nextForm.startDate || !nextForm.endDate) {
      return;
    }

    try {
      const candidates = await apiFetch("/invoices/batch-candidates", {
        method: "POST",
        body: {
          startDate: dateStart(nextForm.startDate),
          endDate: dateEnd(nextForm.endDate),
        },
      });

      setBatchInvoiceCandidates(candidates);
      setBatchInvoiceForm({
        ...nextForm,
        clientIds: candidates.map((client) => client.id),
      });
    } catch (err) {
      setToast(err.message);
    }
  }

  async function loadBatchPayRunCandidates(nextForm = batchPayRunForm) {
    if (!nextForm.startDate || !nextForm.endDate) {
      return;
    }

    try {
      const candidates = await apiFetch("/pay-runs/batch-candidates", {
        method: "POST",
        body: {
          startDate: dateStart(nextForm.startDate),
          endDate: dateEnd(nextForm.endDate),
        },
      });

      setBatchPayRunCandidates(candidates);
      setBatchPayRunForm({
        ...nextForm,
        userIds: candidates.map((user) => user.id),
      });
    } catch (err) {
      setToast(err.message);
    }
  }

  async function openBatchPayRuns() {
    const nextForm = {
      ...batchPayRunBlank,
      ...currentWeekRange(),
    };

    setBatchPayRunForm(nextForm);
    setBatchPayRunCandidates([]);
    setIsBatchPayRunOpen(true);
    await loadBatchPayRunCandidates(nextForm);
  }

  async function generateBatchInvoices() {
    try {
      setIsCreatingInvoiceBatch(true);
      await apiFetch("/invoices/batch", {
        method: "POST",
        body: {
          clientIds: batchInvoiceForm.clientIds,
          startDate: dateStart(batchInvoiceForm.startDate),
          endDate: dateEnd(batchInvoiceForm.endDate),
          dueDate: dueDate(),
        },
      });

      setBatchInvoiceForm(batchInvoiceBlank);
      setBatchInvoiceCandidates([]);
      setIsBatchInvoiceOpen(false);
      await load();
      setToast("Batch invoices generated and sent.");
    } catch (err) {
      setToast(err.message);
    } finally {
      setIsCreatingInvoiceBatch(false);
    }
  }

  async function generateBatchPayRuns() {
    try {
      setIsCreatingPayRunBatch(true);
      await apiFetch("/pay-runs/batch", {
        method: "POST",
        body: {
          userIds: batchPayRunForm.userIds,
          startDate: dateStart(batchPayRunForm.startDate),
          endDate: dateEnd(batchPayRunForm.endDate),
          payDate: payDate(),
        },
      });

      setBatchPayRunForm(batchPayRunBlank);
      setBatchPayRunCandidates([]);
      setIsBatchPayRunOpen(false);
      await load();
      setToast("Batch pay runs generated and sent.");
    } catch (err) {
      setToast(err.message);
    } finally {
      setIsCreatingPayRunBatch(false);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Finance</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <main className="page-pad">
          <section className="panel finance-segment-panel">
            <IonSegment value={segment} onIonChange={(event) => setSegment(event.detail.value || "invoice")}>
              <IonSegmentButton value="invoice">
                <span>Invoice</span>
              </IonSegmentButton>
              <IonSegmentButton value="payroll">
                <span>Payroll</span>
              </IonSegmentButton>
              <IonSegmentButton value="payments">
                <span>Payments</span>
              </IonSegmentButton>
            </IonSegment>
          </section>

          {segment === "invoice" ? (
            <section className="panel form-grid">
              <IonButton expand="block" onClick={openSingleInvoice}>
                Create Invoice
              </IonButton>
              <IonButton expand="block" fill="outline" onClick={() => setIsBatchInvoiceOpen(true)}>
                Batch Invoices
              </IonButton>
              <IonButton expand="block" fill="outline" onClick={() => history.push("/app/finance/invoices/history")}>
                Invoice History
              </IonButton>
            </section>
          ) : segment === "payroll" ? (
            <section className="panel form-grid">
              <IonButton expand="block" onClick={openSinglePayRun}>
                Create Pay Run
              </IonButton>
              <IonButton expand="block" fill="outline" onClick={openBatchPayRuns}>
                Batch Pay Runs
              </IonButton>
              <IonButton expand="block" fill="outline" onClick={() => history.push("/app/finance/payroll/history")}>
                Pay Run History
              </IonButton>
            </section>
          ) : (
            <PaymentsPanel onToast={setToast} />
          )}
        </main>

        <IonModal isOpen={isSingleInvoiceOpen} onDidDismiss={() => setIsSingleInvoiceOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Create Invoice</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setIsSingleInvoiceOpen(false)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <SearchSelect
                  label="Clients"
                  placeholder="Select clients"
                  multiple
                  value={singleInvoiceForm.clientIds}
                  onChange={(clientIds) => {
                    setSingleInvoiceForm({ ...singleInvoiceForm, clientIds });
                    setCombinedInvoiceDetails(combinedInvoiceDetailsBlank);
                    setPreviewInvoice(null);
                  }}
                  options={clients.map((client) => ({ value: client.id, label: client.name }))}
                />
                <IonInput
                  className="metora-input"
                  label="Start Date"
                  labelPlacement="stacked"
                  type="date"
                  value={singleInvoiceForm.startDate}
                  onIonInput={(event) => {
                    setSingleInvoiceForm({ ...singleInvoiceForm, startDate: event.detail.value ?? "" });
                    setPreviewInvoice(null);
                  }}
                />
                <IonInput
                  className="metora-input"
                  label="End Date"
                  labelPlacement="stacked"
                  type="date"
                  value={singleInvoiceForm.endDate}
                  onIonInput={(event) => {
                    setSingleInvoiceForm({ ...singleInvoiceForm, endDate: event.detail.value ?? "" });
                    setPreviewInvoice(null);
                  }}
                />
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={generateSingleInvoicePreview}
                  disabled={isGeneratingInvoicePreview || singleInvoiceForm.clientIds.length === 0 || !singleInvoiceForm.startDate || !singleInvoiceForm.endDate}
                >
                  Generate PDF Preview
                </IonButton>
                {previewInvoice && (
                  <>
                    <IonButton
                      expand="block"
                      fill="outline"
                      onClick={() => downloadInvoice(previewInvoice)}
                      disabled={isGeneratingInvoicePreview || isSendingInvoice}
                    >
                      Download Invoice
                    </IonButton>
                    <IonButton
                      expand="block"
                      onClick={() => sendInvoice(previewInvoice.id)}
                      disabled={isGeneratingInvoicePreview || isSendingInvoice}
                    >
                      Send This Invoice
                    </IonButton>
                  </>
                )}
              </section>
              <PdfInvoicePreview invoiceId={previewInvoice?.id} isPreparing={isGeneratingInvoicePreview} />
            </main>
          </IonContent>
        </IonModal>

        <IonModal isOpen={isCombinedInvoiceDetailsOpen} onDidDismiss={() => setIsCombinedInvoiceDetailsOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Combined Invoice Details</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setIsCombinedInvoiceDetailsOpen(false)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <SearchSelect
                  label="Name on Invoice"
                  placeholder="Select invoice name"
                  value={combinedSelectedValue(combinedInvoiceDetails.recipientName, selectedClientOptions((client) => client.invoiceName || client.name))}
                  onChange={(value) => setCombinedInvoiceDetails({ ...combinedInvoiceDetails, recipientName: combinedValueFromOption(value) })}
                  options={selectedClientOptions((client) => client.invoiceName || client.name)}
                />
                <SearchSelect
                  label="Address"
                  placeholder="Select address"
                  value={combinedSelectedValue(combinedInvoiceDetails.recipientAddress, selectedClientOptions((client) => client.address))}
                  onChange={(value) => setCombinedInvoiceDetails({ ...combinedInvoiceDetails, recipientAddress: combinedValueFromOption(value) })}
                  options={selectedClientOptions((client) => client.address)}
                />
                <SearchSelect
                  label="Email"
                  placeholder="Select email"
                  value={combinedSelectedValue(combinedInvoiceDetails.recipientEmail, selectedClientOptions((client) => client.email))}
                  onChange={(value) => setCombinedInvoiceDetails({ ...combinedInvoiceDetails, recipientEmail: combinedValueFromOption(value) })}
                  options={selectedClientOptions((client) => client.email)}
                />
                <SearchSelect
                  label="Phone"
                  placeholder="Select phone"
                  value={combinedSelectedValue(combinedInvoiceDetails.recipientPhone, selectedClientOptions((client) => client.phone))}
                  onChange={(value) => setCombinedInvoiceDetails({ ...combinedInvoiceDetails, recipientPhone: combinedValueFromOption(value) })}
                  options={selectedClientOptions((client) => client.phone)}
                />
                <IonButton
                  expand="block"
                  onClick={generateCombinedInvoicePreview}
                  disabled={
                    isGeneratingInvoicePreview ||
                    !combinedInvoiceDetails.recipientName ||
                    !combinedInvoiceDetails.recipientAddress ||
                    !combinedInvoiceDetails.recipientEmail
                  }
                >
                  Generate PDF Preview
                </IonButton>
              </section>
            </main>
          </IonContent>
        </IonModal>

        <IonModal isOpen={isSinglePayRunOpen} onDidDismiss={() => setIsSinglePayRunOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Create Pay Run</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setIsSinglePayRunOpen(false)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <SearchSelect
                  label="Employee"
                  placeholder="Select employee"
                  value={singlePayRunForm.userId}
                  onChange={(userId) => {
                    setSinglePayRunForm({ ...singlePayRunForm, userId });
                    setPreviewPayRun(null);
                  }}
                  options={users.map((user) => ({
                    value: user.id,
                    label: `${user.name}${user.email ? ` • ${user.email}` : ""}`,
                  }))}
                />
                <IonInput
                  className="metora-input"
                  label="Start Date"
                  labelPlacement="stacked"
                  type="date"
                  value={singlePayRunForm.startDate}
                  onIonInput={(event) => {
                    setSinglePayRunForm({ ...singlePayRunForm, startDate: event.detail.value ?? "" });
                    setPreviewPayRun(null);
                  }}
                />
                <IonInput
                  className="metora-input"
                  label="End Date"
                  labelPlacement="stacked"
                  type="date"
                  value={singlePayRunForm.endDate}
                  onIonInput={(event) => {
                    setSinglePayRunForm({ ...singlePayRunForm, endDate: event.detail.value ?? "" });
                    setPreviewPayRun(null);
                  }}
                />
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={generateSinglePayRunPreview}
                  disabled={isGeneratingPayRunPreview || !singlePayRunForm.userId || !singlePayRunForm.startDate || !singlePayRunForm.endDate}
                >
                  Generate PDF Preview
                </IonButton>
                {previewPayRun && (
                  <>
                    <IonButton
                      expand="block"
                      fill="outline"
                      onClick={() => downloadPayRun(previewPayRun)}
                      disabled={isGeneratingPayRunPreview || isSendingPayRun}
                    >
                      Download Pay Run
                    </IonButton>
                    <IonButton
                      expand="block"
                      onClick={() => sendPayRun(previewPayRun.id)}
                      disabled={isGeneratingPayRunPreview || isSendingPayRun}
                    >
                      Send This Pay Run
                    </IonButton>
                  </>
                )}
              </section>
              <PdfPayRunPreview payRunId={previewPayRun?.id} isPreparing={isGeneratingPayRunPreview} />
            </main>
          </IonContent>
        </IonModal>

        <IonModal isOpen={isBatchInvoiceOpen} onDidDismiss={() => setIsBatchInvoiceOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Batch Invoices</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setIsBatchInvoiceOpen(false)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <IonInput
                  className="metora-input"
                  label="Start Date"
                  labelPlacement="stacked"
                  type="date"
                  value={batchInvoiceForm.startDate}
                  onIonInput={(event) => {
                    const nextForm = { ...batchInvoiceForm, startDate: event.detail.value ?? "" };
                    setBatchInvoiceForm(nextForm);
                    loadBatchInvoiceCandidates(nextForm);
                  }}
                />
                <IonInput
                  className="metora-input"
                  label="End Date"
                  labelPlacement="stacked"
                  type="date"
                  value={batchInvoiceForm.endDate}
                  onIonInput={(event) => {
                    const nextForm = { ...batchInvoiceForm, endDate: event.detail.value ?? "" };
                    setBatchInvoiceForm(nextForm);
                    loadBatchInvoiceCandidates(nextForm);
                  }}
                />
                <SearchSelect
                  label="Clients"
                  placeholder="Select clients"
                  multiple
                  value={batchInvoiceForm.clientIds}
                  onChange={(clientIds) => setBatchInvoiceForm({ ...batchInvoiceForm, clientIds })}
                  options={batchInvoiceCandidates.map((client) => ({ value: client.id, label: client.label }))}
                />
                <IonButton
                  expand="block"
                  onClick={generateBatchInvoices}
                  disabled={isCreatingInvoiceBatch || batchInvoiceForm.clientIds.length === 0}
                >
                  Generate and Send Batch
                </IonButton>
              </section>
            </main>
          </IonContent>
        </IonModal>

        <IonModal isOpen={isBatchPayRunOpen} onDidDismiss={() => setIsBatchPayRunOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Batch Pay Runs</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setIsBatchPayRunOpen(false)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <IonInput
                  className="metora-input"
                  label="Start Date"
                  labelPlacement="stacked"
                  type="date"
                  value={batchPayRunForm.startDate}
                  onIonInput={(event) => {
                    const nextForm = { ...batchPayRunForm, startDate: event.detail.value ?? "" };
                    setBatchPayRunForm(nextForm);
                    loadBatchPayRunCandidates(nextForm);
                  }}
                />
                <IonInput
                  className="metora-input"
                  label="End Date"
                  labelPlacement="stacked"
                  type="date"
                  value={batchPayRunForm.endDate}
                  onIonInput={(event) => {
                    const nextForm = { ...batchPayRunForm, endDate: event.detail.value ?? "" };
                    setBatchPayRunForm(nextForm);
                    loadBatchPayRunCandidates(nextForm);
                  }}
                />
                <SearchSelect
                  label="Employees"
                  placeholder="Select employees"
                  multiple
                  value={batchPayRunForm.userIds}
                  onChange={(userIds) => setBatchPayRunForm({ ...batchPayRunForm, userIds })}
                  options={batchPayRunCandidates.map((user) => ({
                    value: user.id,
                    label: `${user.name}${user.email ? ` • ${user.email}` : ""}`,
                  }))}
                />
                <IonButton
                  expand="block"
                  onClick={generateBatchPayRuns}
                  disabled={isCreatingPayRunBatch || batchPayRunForm.userIds.length === 0}
                >
                  Generate and Send Batch
                </IonButton>
              </section>
            </main>
          </IonContent>
        </IonModal>

        <IonToast isOpen={Boolean(toast)} message={toast} duration={2500} onDidDismiss={() => setToast("")} />
      </IonContent>
    </IonPage>
  );
}
